import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  GraduationCap, CheckCircle2, Circle, PlayCircle, Award, Users2, Sparkles,
  Plus, Pencil, Trash2, Loader2, ArrowLeft, ClipboardList, BookOpen, RefreshCw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import api, { formatApiError } from "../lib/api";

const KIND_LABEL = { service: "Service", standards: "Standards", selling: "Selling", tools: "Tools & Systems" };
const KIND_ORDER = ["standards", "tools", "selling", "service"];
const toLines = (arr) => (arr || []).join("\n");
const fromLines = (t) => (t || "").split("\n").map((s) => s.trim()).filter(Boolean);

function embedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

export default function Academy({ isOwner }) {
  const [view, setView] = useState("learn");
  return (
    <div data-testid="academy">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-6 w-6 text-iri-mid" strokeWidth={1.5} />
          <h2 className="font-heading text-2xl font-light text-ink">AestheticAcademy</h2>
        </div>
        {isOwner && (
          <div className="flex gap-2">
            {[["learn", "Learn", BookOpen], ["manage", "Manage content", Pencil], ["team", "Team progress", Users2]].map(([k, label, Icon]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`academy-view-${k}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${view === k ? "iri-gradient text-ink font-medium" : "text-[#8A9492] hover:text-ink border border-slate-200"}`}>
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
        )}
      </div>
      {view === "learn" && <LearnView isOwner={isOwner} />}
      {view === "manage" && isOwner && <ManageView />}
      {view === "team" && isOwner && <TeamView />}
    </div>
  );
}

/* ------------------------------------------------------------------ LEARN */
function LearnView() {
  const [me, setMe] = useState(null);
  const [track, setTrack] = useState(null);
  const [modules, setModules] = useState(null);
  const [openId, setOpenId] = useState(null);

  const loadMe = () => api.get("/academy/me").then((r) => {
    setMe(r.data);
    setTrack((t) => t || r.data.track || r.data.tracks[0]?.slug);
  });
  useEffect(() => { loadMe(); }, []);
  useEffect(() => {
    if (!track) return;
    api.get(`/academy/modules?track=${track}`).then((r) => setModules(r.data.modules));
  }, [track]);

  const refresh = () => { loadMe(); if (track) api.get(`/academy/modules?track=${track}`).then((r) => setModules(r.data.modules)); };

  if (openId) return <ModuleView id={openId} onBack={() => { setOpenId(null); refresh(); }} />;
  if (!me) return <p className="text-sm text-[#8A9492] p-8 text-center">Loading…</p>;

  const summary = me.summary.find((s) => s.track === track);
  const grouped = {};
  (modules || []).forEach((m) => { (grouped[m.kind] = grouped[m.kind] || []).push(m); });

  return (
    <div className="space-y-6" data-testid="academy-learn">
      <div className="flex gap-2 flex-wrap">
        {me.tracks.map((t) => {
          const s = me.summary.find((x) => x.track === t.slug);
          return (
            <button key={t.slug} onClick={() => { setTrack(t.slug); setModules(null); }} data-testid={`academy-track-${t.slug}`}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${track === t.slug ? "border-iri-mid text-ink font-medium bg-white" : "border-slate-200 text-[#8A9492] hover:text-ink"}`}>
              {t.label} <span className="text-xs text-[#8A9492]">· {s?.pct || 0}%</span>
            </button>
          );
        })}
      </div>

      {summary && (
        <div className="rounded-2xl bg-white border border-slate-100 p-5" data-testid="academy-progress">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-ink font-medium">{summary.label} track — {summary.completed} of {summary.total} complete</p>
            {summary.total > 0 && summary.completed === summary.total && (
              <CertificateButton track={track} />
            )}
          </div>
          <div className="h-2.5 rounded-full bg-stone overflow-hidden">
            <div className="h-full iri-gradient transition-all" style={{ width: `${summary.pct}%` }} />
          </div>
        </div>
      )}

      {!modules ? <p className="text-sm text-[#8A9492] p-8 text-center">Loading modules…</p> :
        modules.length === 0 ? <p className="text-sm text-[#8A9492] p-8 text-center">No modules in this track yet.</p> :
        KIND_ORDER.filter((k) => grouped[k]).map((k) => (
          <div key={k}>
            <p className="text-xs uppercase tracking-wider text-[#8A9492] mb-2">{KIND_LABEL[k]}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {grouped[k].map((m) => (
                <button key={m.id} onClick={() => setOpenId(m.id)} data-testid={`academy-module-${m.id}`}
                  className="text-left rounded-2xl bg-white border border-slate-100 p-5 hover:border-iri-mid transition-colors flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-ink font-medium leading-snug">{m.title}</p>
                    {m.status === "completed"
                      ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                      : m.status === "in_progress"
                        ? <PlayCircle className="h-5 w-5 text-iri-mid shrink-0" />
                        : <Circle className="h-5 w-5 text-slate-300 shrink-0" />}
                  </div>
                  <p className="text-sm text-[#8A9492] mt-2 flex-1">{m.summary}</p>
                  <p className="text-xs text-[#8A9492] mt-3">
                    {m.has_quiz ? "Includes quiz" : "Reading only"}
                    {m.best_score != null && ` · best ${m.best_score}%`}
                  </p>
                </button>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}

function CertificateButton({ track }) {
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/academy/certificate?track=${track}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `iridescent-academy-${track}-certificate.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Complete all modules first"); }
    finally { setBusy(false); }
  };
  return (
    <Button onClick={dl} disabled={busy} data-testid="academy-certificate-btn" className="iri-gradient text-ink rounded-full h-9 px-4 text-sm">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Award className="h-4 w-4 mr-1.5" /> Certificate</>}
    </Button>
  );
}

/* ------------------------------------------------------------------ MODULE VIEWER + QUIZ */
function ModuleView({ id, onBack }) {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get(`/academy/modules/${id}`).then((r) => setM(r.data));
    api.post(`/academy/modules/${id}/progress`).catch(() => {});
  }, [id]);
  if (!m) return <p className="text-sm text-[#8A9492] p-8 text-center">Loading…</p>;
  const hasQuiz = (m.quiz?.questions || []).length > 0;

  return (
    <div className="space-y-6" data-testid="academy-module-view">
      <button onClick={onBack} data-testid="academy-back" className="inline-flex items-center gap-2 text-sm text-[#8A9492] hover:text-ink">
        <ArrowLeft className="h-4 w-4" /> Back to modules
      </button>
      <div>
        <p className="text-xs uppercase tracking-wider text-iri-mid">{KIND_LABEL[m.kind]}</p>
        <h3 className="font-heading text-3xl font-light text-ink mt-1">{m.title}</h3>
        {m.summary && <p className="text-[#5C6664] mt-2">{m.summary}</p>}
      </div>

      {(m.sections || []).map((s, i) => {
        const emb = embedUrl(s.video_url);
        return (
          <div key={i} className="rounded-2xl bg-white border border-slate-100 p-6">
            {s.heading && <h4 className="font-heading text-xl text-ink mb-2">{s.heading}</h4>}
            {(s.body || "").split("\n").map((line, j) => line.trim() && <p key={j} className="text-[#5C6664] leading-relaxed mb-1.5">{line}</p>)}
            {emb && (
              <div className="mt-4 rounded-xl overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
                <iframe src={emb} title={s.heading || "video"} className="w-full h-full" allowFullScreen frameBorder="0" />
              </div>
            )}
          </div>
        );
      })}

      {(m.talking_points || []).length > 0 && <ListCard title="Client talking points" items={m.talking_points} />}
      {(m.pre_care || []).length > 0 && <ListCard title="Pre-care" items={m.pre_care} />}
      {(m.post_care || []).length > 0 && <ListCard title="Post-care / aftercare" items={m.post_care} />}

      {(m.before_afters || []).length > 0 && (
        <div className="rounded-2xl bg-white border border-slate-100 p-6">
          <h4 className="font-heading text-xl text-ink mb-3">Before &amp; after</h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {m.before_afters.map((ba, i) => (
              <div key={i} className="grid grid-cols-2 gap-1">
                <img src={ba.before_url} alt="before" className="rounded-lg w-full h-32 object-cover" />
                <img src={ba.after_url} alt="after" className="rounded-lg w-full h-32 object-cover" />
              </div>
            ))}
          </div>
        </div>
      )}

      {hasQuiz
        ? <Quiz module={m} onDone={() => api.get(`/academy/modules/${id}`).then((r) => setM(r.data))} />
        : <MarkComplete id={id} status={m.progress?.status} onDone={() => api.get(`/academy/modules/${id}`).then((r) => setM(r.data))} />}
    </div>
  );
}

function ListCard({ title, items }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-6">
      <h4 className="font-heading text-xl text-ink mb-2">{title}</h4>
      <ul className="list-disc pl-5 space-y-1 text-[#5C6664]">
        {items.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
    </div>
  );
}

function MarkComplete({ id, status, onDone }) {
  const [busy, setBusy] = useState(false);
  const done = status === "completed";
  const go = async () => {
    setBusy(true);
    try { await api.post(`/academy/modules/${id}/complete`); toast.success("Module marked complete"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-6 text-center">
      {done
        ? <p className="text-emerald-600 font-medium flex items-center justify-center gap-2"><CheckCircle2 className="h-5 w-5" /> Completed</p>
        : <Button onClick={go} disabled={busy} data-testid="academy-mark-complete" className="iri-gradient text-ink rounded-full h-11 px-8">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mark as complete"}
          </Button>}
    </div>
  );
}

function Quiz({ module, onDone }) {
  const questions = module.quiz?.questions || [];
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (Object.keys(answers).length < questions.length) { toast.error("Answer every question"); return; }
    setBusy(true);
    try {
      const arr = questions.map((_, i) => (answers[i] != null ? answers[i] : -1));
      const { data } = await api.post(`/academy/modules/${module.id}/quiz`, { answers: arr });
      setResult(data);
      if (data.passed) { toast.success(`Passed — ${data.score}%!`); onDone(); }
      else toast.error(`${data.score}% — need ${data.pass_pct}% to pass`);
    } catch (e) { toast.error("Could not submit quiz"); }
    finally { setBusy(false); }
  };
  const retake = () => { setAnswers({}); setResult(null); };

  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-6" data-testid="academy-quiz">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardList className="h-5 w-5 text-iri-mid" />
        <h4 className="font-heading text-xl text-ink">Quiz <span className="text-sm text-[#8A9492]">· pass {module.quiz.pass_pct}%</span></h4>
      </div>
      <div className="space-y-5">
        {questions.map((q, i) => {
          const res = result?.results?.[i];
          return (
            <div key={i} data-testid={`quiz-q-${i}`}>
              <p className="text-ink font-medium mb-2">{i + 1}. {q.q}</p>
              <div className="space-y-1.5">
                {q.choices.map((c, ci) => {
                  const chosen = answers[i] === ci;
                  let ring = chosen ? "border-iri-mid bg-iri-light/10" : "border-slate-200";
                  if (res) {
                    if (ci === res.answer) ring = "border-emerald-400 bg-emerald-50";
                    else if (ci === res.your_answer) ring = "border-rose-300 bg-rose-50";
                  }
                  return (
                    <label key={ci} data-testid={`quiz-q-${i}-choice-${ci}`}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer text-sm ${ring}`}>
                      <input type="radio" name={`q${i}`} disabled={!!result} checked={chosen}
                        onChange={() => setAnswers((a) => ({ ...a, [i]: ci }))} className="accent-iri-mid" />
                      <span className="text-[#5C6664]">{c}</span>
                    </label>
                  );
                })}
              </div>
              {res && res.explanation && <p className="text-xs text-[#8A9492] mt-1.5">{res.explanation}</p>}
            </div>
          );
        })}
      </div>
      <div className="mt-5 flex items-center gap-3">
        {!result
          ? <Button onClick={submit} disabled={busy} data-testid="academy-quiz-submit" className="iri-gradient text-ink rounded-full h-11 px-8">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit quiz"}
            </Button>
          : <>
              <p className={`font-medium ${result.passed ? "text-emerald-600" : "text-rose-500"}`}>
                {result.passed ? "Passed" : "Not passed"} — {result.score}% ({result.correct}/{result.total})
              </p>
              <Button onClick={retake} variant="outline" data-testid="academy-quiz-retake" className="rounded-full h-10 px-5 border-slate-200">Retake</Button>
            </>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ OWNER: MANAGE CONTENT */
function ManageView() {
  const [modules, setModules] = useState(null);
  const [editing, setEditing] = useState(null); // module object or "new"
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const load = () => api.get("/academy/modules").then((r) => setModules(r.data.modules));
  useEffect(() => { load(); api.get("/academy/generate-quizzes/status").then((r) => setJob(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (job?.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/academy/generate-quizzes/status");
        setJob(data);
        if (data.status !== "running") {
          clearInterval(t);
          load();
          toast.success(`Quizzes generated: ${data.done - (data.failed || 0)} ok${data.failed ? `, ${data.failed} failed` : ""}`);
        }
      } catch (e) { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [job?.status]);

  const genQuizzes = async () => {
    try {
      const { data } = await api.post("/academy/generate-quizzes");
      setJob(data);
      if (data.status === "done" && data.total === 0) toast.success("All service modules already have quizzes");
      else toast.success("Generating quizzes in the background — you can keep working");
    } catch (e) { toast.error("Could not start quiz generation"); }
  };

  const reseed = async () => {
    setBusy(true);
    try { const { data } = await api.post("/academy/reseed"); toast.success(`Seed check: +${data.inserted} new`); load(); }
    catch (e) { toast.error("Reseed failed"); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this module and everyone's progress on it?")) return;
    await api.delete(`/academy/modules/${id}`); toast.success("Deleted"); load();
  };

  if (editing) return <ModuleEditor initial={editing === "new" ? null : editing} onClose={() => { setEditing(null); load(); }} />;
  if (!modules) return <p className="text-sm text-[#8A9492] p-8 text-center">Loading…</p>;

  const grouped = {};
  modules.forEach((m) => { (grouped[m.kind] = grouped[m.kind] || []).push(m); });

  return (
    <div className="space-y-6" data-testid="academy-manage">
      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => setEditing("new")} data-testid="academy-new-module" className="iri-gradient text-ink rounded-full h-10 px-5"><Plus className="h-4 w-4 mr-1.5" /> New module</Button>
        <Button onClick={reseed} disabled={busy} variant="outline" data-testid="academy-reseed" className="rounded-full h-10 px-5 border-slate-200"><RefreshCw className="h-4 w-4 mr-1.5" /> Re-sync service modules</Button>
        <Button onClick={genQuizzes} disabled={job?.status === "running"} data-testid="academy-gen-quizzes" className="rounded-full h-10 px-5 bg-[#241f38] text-white hover:bg-[#241f38]/90">
          {job?.status === "running"
            ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Generating quizzes {job.done}/{job.total}…</>
            : <><Sparkles className="h-4 w-4 mr-1.5" /> AI: quizzes for all services</>}
        </Button>
      </div>
      {KIND_ORDER.filter((k) => grouped[k]).map((k) => (
        <div key={k}>
          <p className="text-xs uppercase tracking-wider text-[#8A9492] mb-2">{KIND_LABEL[k]} ({grouped[k].length})</p>
          <div className="space-y-2">
            {grouped[k].map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 rounded-xl bg-white border border-slate-100 px-4 py-3" data-testid={`academy-manage-${m.id}`}>
                <div className="min-w-0">
                  <p className="text-ink font-medium truncate">{m.title} {!m.published && <span className="text-xs text-amber-600">· draft</span>}</p>
                  <p className="text-xs text-[#8A9492]">{(m.tracks || []).join(", ") || "no tracks"} · {m.has_quiz ? "quiz" : "no quiz"}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button onClick={() => setEditing(m)} variant="ghost" size="icon" data-testid={`academy-edit-${m.id}`} className="h-9 w-9 rounded-full"><Pencil className="h-4 w-4" /></Button>
                  <Button onClick={() => del(m.id)} variant="ghost" size="icon" data-testid={`academy-delete-${m.id}`} className="h-9 w-9 rounded-full text-rose-500"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const EMPTY = { title: "", kind: "standards", tracks: [], service_slug: "", order: 100, summary: "",
  sections: [{ heading: "", body: "", video_url: "" }], talking_points: [], pre_care: [], post_care: [],
  before_afters: [], quiz: { pass_pct: 80, questions: [] }, published: true };

function ModuleEditor({ initial, onClose }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState(false);
  const TRACKS = [["front-desk", "Front Desk"], ["provider", "Provider"], ["specialist", "Treatment Specialist"], ["sales", "Sales"]];

  useEffect(() => {
    if (initial) api.get(`/academy/modules/${initial.id}`).then((r) => setF({ ...EMPTY, ...r.data }));
    else setF({ ...EMPTY });
  }, [initial]);
  if (!f) return <p className="text-sm text-[#8A9492] p-8 text-center">Loading…</p>;

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleTrack = (t) => setF((s) => ({ ...s, tracks: s.tracks.includes(t) ? s.tracks.filter((x) => x !== t) : [...s.tracks, t] }));

  const aiGenerate = async () => {
    setGen(true);
    try {
      const { data } = await api.post("/academy/generate", { kind: f.kind, title: f.title || null, service_slug: f.service_slug || null });
      setF((s) => ({ ...s, title: data.title || s.title, summary: data.summary || s.summary,
        sections: data.sections?.length ? data.sections : s.sections,
        talking_points: data.talking_points || s.talking_points, pre_care: data.pre_care || s.pre_care,
        post_care: data.post_care || s.post_care, quiz: data.quiz || s.quiz }));
      toast.success("AI draft loaded — review & save");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI generation failed"); }
    finally { setGen(false); }
  };

  const save = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    const payload = { ...f, service_slug: f.service_slug || null };
    try {
      if (initial) await api.put(`/academy/modules/${initial.id}`, payload);
      else await api.post("/academy/modules", payload);
      toast.success("Saved"); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  // section helpers
  const setSection = (i, k, v) => setF((s) => ({ ...s, sections: s.sections.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const addSection = () => setF((s) => ({ ...s, sections: [...s.sections, { heading: "", body: "", video_url: "" }] }));
  const rmSection = (i) => setF((s) => ({ ...s, sections: s.sections.filter((_, j) => j !== i) }));
  // quiz helpers
  const setQ = (i, k, v) => setF((s) => ({ ...s, quiz: { ...s.quiz, questions: s.quiz.questions.map((q, j) => j === i ? { ...q, [k]: v } : q) } }));
  const setChoice = (i, ci, v) => setQ(i, "choices", f.quiz.questions[i].choices.map((c, j) => j === ci ? v : c));
  const addQ = () => setF((s) => ({ ...s, quiz: { ...s.quiz, questions: [...s.quiz.questions, { q: "", choices: ["", ""], answer: 0, explanation: "" }] } }));
  const rmQ = (i) => setF((s) => ({ ...s, quiz: { ...s.quiz, questions: s.quiz.questions.filter((_, j) => j !== i) } }));
  const addChoice = (i) => setQ(i, "choices", [...f.quiz.questions[i].choices, ""]);

  const field = "rounded-lg mt-1";
  return (
    <div className="space-y-5" data-testid="academy-editor">
      <button onClick={onClose} className="inline-flex items-center gap-2 text-sm text-[#8A9492] hover:text-ink"><ArrowLeft className="h-4 w-4" /> Back</button>

      <div className="rounded-2xl bg-white border border-slate-100 p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm text-ink">Title</label><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="editor-title" className={field} /></div>
          <div><label className="text-sm text-ink">Type</label>
            <select value={f.kind} onChange={(e) => set("kind", e.target.value)} data-testid="editor-kind" className="w-full h-10 rounded-lg border border-slate-200 mt-1 px-3 text-sm">
              {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm text-ink">Service slug (for service modules)</label><Input value={f.service_slug || ""} onChange={(e) => set("service_slug", e.target.value)} data-testid="editor-service-slug" placeholder="e.g. microneedling" className={field} /></div>
          <div><label className="text-sm text-ink">Order</label><Input type="number" value={f.order} onChange={(e) => set("order", Number(e.target.value))} className={field} /></div>
        </div>
        <div>
          <label className="text-sm text-ink">Tracks</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {TRACKS.map(([t, l]) => (
              <button key={t} onClick={() => toggleTrack(t)} data-testid={`editor-track-${t}`}
                className={`px-3 py-1.5 rounded-full text-sm border ${f.tracks.includes(t) ? "border-iri-mid bg-iri-light/10 text-ink" : "border-slate-200 text-[#8A9492]"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div><label className="text-sm text-ink">Summary</label><Input value={f.summary} onChange={(e) => set("summary", e.target.value)} className={field} /></div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={aiGenerate} disabled={gen} data-testid="editor-ai-generate" className="rounded-full h-10 px-5 bg-[#241f38] text-white hover:bg-[#241f38]/90">
            {gen ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Generate draft with AI
          </Button>
          <label className="flex items-center gap-2 text-sm text-[#5C6664]">
            <input type="checkbox" checked={f.published} onChange={(e) => set("published", e.target.checked)} className="accent-iri-mid" /> Published
          </label>
        </div>
      </div>

      {/* Sections */}
      <div className="rounded-2xl bg-white border border-slate-100 p-6 space-y-4">
        <p className="font-heading text-lg text-ink">Lesson sections</p>
        {f.sections.map((s, i) => (
          <div key={i} className="rounded-xl border border-slate-100 p-4 space-y-2" data-testid={`editor-section-${i}`}>
            <Input value={s.heading} onChange={(e) => setSection(i, "heading", e.target.value)} placeholder="Heading" className="rounded-lg" />
            <Textarea value={s.body} onChange={(e) => setSection(i, "body", e.target.value)} placeholder="Body (use new lines / • for bullets)" className="rounded-lg min-h-24" />
            <Input value={s.video_url || ""} onChange={(e) => setSection(i, "video_url", e.target.value)} placeholder="YouTube / Vimeo URL (optional)" className="rounded-lg" />
            <button onClick={() => rmSection(i)} className="text-xs text-rose-500">Remove section</button>
          </div>
        ))}
        <Button onClick={addSection} variant="outline" className="rounded-full h-9 px-4 border-slate-200 text-sm"><Plus className="h-4 w-4 mr-1" /> Add section</Button>
      </div>

      {/* List fields */}
      <div className="rounded-2xl bg-white border border-slate-100 p-6 grid sm:grid-cols-3 gap-4">
        {[["talking_points", "Client talking points"], ["pre_care", "Pre-care"], ["post_care", "Post-care"]].map(([k, l]) => (
          <div key={k}>
            <label className="text-sm text-ink">{l} <span className="text-xs text-[#8A9492]">(one per line)</span></label>
            <Textarea value={toLines(f[k])} onChange={(e) => set(k, fromLines(e.target.value))} className="rounded-lg mt-1 min-h-24" />
          </div>
        ))}
      </div>

      {/* Quiz */}
      <div className="rounded-2xl bg-white border border-slate-100 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <p className="font-heading text-lg text-ink">Quiz</p>
          <label className="text-sm text-[#5C6664]">Pass %
            <input type="number" value={f.quiz.pass_pct} onChange={(e) => set("quiz", { ...f.quiz, pass_pct: Number(e.target.value) })} className="w-16 ml-2 h-8 rounded border border-slate-200 px-2" />
          </label>
        </div>
        {f.quiz.questions.map((q, i) => (
          <div key={i} className="rounded-xl border border-slate-100 p-4 space-y-2" data-testid={`editor-q-${i}`}>
            <Input value={q.q} onChange={(e) => setQ(i, "q", e.target.value)} placeholder="Question" className="rounded-lg" />
            {q.choices.map((c, ci) => (
              <div key={ci} className="flex items-center gap-2">
                <input type="radio" name={`ans${i}`} checked={q.answer === ci} onChange={() => setQ(i, "answer", ci)} className="accent-emerald-500" title="Correct answer" />
                <Input value={c} onChange={(e) => setChoice(i, ci, e.target.value)} placeholder={`Choice ${ci + 1}`} className="rounded-lg" />
              </div>
            ))}
            <div className="flex items-center gap-3">
              <button onClick={() => addChoice(i)} className="text-xs text-iri-mid">+ choice</button>
              <button onClick={() => rmQ(i)} className="text-xs text-rose-500">Remove question</button>
            </div>
            <Input value={q.explanation || ""} onChange={(e) => setQ(i, "explanation", e.target.value)} placeholder="Explanation (shown after answering)" className="rounded-lg" />
          </div>
        ))}
        <Button onClick={addQ} variant="outline" className="rounded-full h-9 px-4 border-slate-200 text-sm"><Plus className="h-4 w-4 mr-1" /> Add question</Button>
      </div>

      <div className="flex gap-3">
        <Button onClick={save} disabled={busy} data-testid="editor-save" className="iri-gradient text-ink rounded-full h-11 px-8">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save module"}</Button>
        <Button onClick={onClose} variant="outline" className="rounded-full h-11 px-6 border-slate-200">Cancel</Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ OWNER: TEAM PROGRESS */
function TeamView() {
  const [data, setData] = useState(null);
  const load = () => api.get("/academy/team").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <p className="text-sm text-[#8A9492] p-8 text-center">Loading…</p>;

  const assign = async (sid, track) => {
    try { await api.put(`/academy/staff/${sid}/track`, { track: track || null }); toast.success("Track assigned"); load(); }
    catch (e) { toast.error("Failed to assign"); }
  };

  return (
    <div className="space-y-3" data-testid="academy-team">
      {data.staff.length === 0 && <p className="text-sm text-[#8A9492] p-8 text-center">No staff yet.</p>}
      {data.staff.map((s) => (
        <div key={s.id} className="rounded-2xl bg-white border border-slate-100 p-5" data-testid={`team-staff-${s.id}`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-ink font-medium">{s.name}</p>
              <p className="text-xs text-[#8A9492]">{s.email}</p>
            </div>
            <label className="text-sm text-[#5C6664]">Assigned track:
              <select value={s.academy_track || ""} onChange={(e) => assign(s.id, e.target.value)} data-testid={`team-assign-${s.id}`}
                className="ml-2 h-9 rounded-lg border border-slate-200 px-2 text-sm">
                <option value="">— none —</option>
                {data.tracks.map((t) => <option key={t.slug} value={t.slug}>{t.label}</option>)}
              </select>
            </label>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            {s.tracks.map((t) => (
              <div key={t.track} className={`rounded-xl p-3 ${s.academy_track === t.track ? "bg-iri-light/10 border border-iri-mid" : "bg-stone"}`}>
                <p className="text-xs text-[#8A9492]">{t.label}</p>
                <p className="text-ink font-medium">{t.pct}% <span className="text-xs text-[#8A9492]">({t.completed}/{t.total})</span></p>
                <div className="h-1.5 rounded-full bg-white mt-1.5 overflow-hidden"><div className="h-full iri-gradient" style={{ width: `${t.pct}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
